import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, MessageCircle, Instagram, Facebook, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { UKGM_LINKS } from '@/lib/ui';
import { useSEO } from '@/lib/useSEO';

const OFFICE_ADDRESS = 'Room No. 1488, BMC Colony, A.H. Marg, 6 Number, Malvani, Malad (West), Mumbai – 400095, Maharashtra, India';
const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(OFFICE_ADDRESS);

export default function Contact() {
  const { t } = useTranslation();
  useSEO({ title: 'Contact', description: 'Contact Universal Krishna Gaukul Mission (UKGM). Office: Malvani, Malad (West), Mumbai. Email ukgm.trust@gmail.com or call +91 74472 61155.' });
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/contact', form);
      toast.success(t('contact.form.success'));
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch { toast.error(t('contact.form.error')); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="contact-page">
      <h1 className="font-serif text-4xl md:text-6xl text-ukgm-blue font-semibold leading-tight">{t('contact.title')}</h1>
      <p className="mt-4 max-w-2xl text-ukgm-slate text-lg">{t('contact.subtitle')}</p>

      <div className="mt-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl border border-ukgm-gold/25 p-6 md:p-8 space-y-4" data-testid="contact-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('contact.form.name')}</label>
              <input required value={form.name} onChange={set('name')} className="mt-2 w-full px-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="contact-name" />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('contact.form.email')}</label>
              <input required type="email" value={form.email} onChange={set('email')} className="mt-2 w-full px-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="contact-email" />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('contact.form.phone')}</label>
              <input value={form.phone} onChange={set('phone')} className="mt-2 w-full px-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="contact-phone" />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('contact.form.subject')}</label>
              <input required value={form.subject} onChange={set('subject')} className="mt-2 w-full px-4 py-3 rounded-full bg-ukgm-cream text-sm" data-testid="contact-subject" />
            </div>
          </div>
          <div>
            <label className="text-xs tracking-widest uppercase text-ukgm-slate font-bold">{t('contact.form.message')}</label>
            <textarea required value={form.message} onChange={set('message')} rows={5} className="mt-2 w-full px-4 py-3 rounded-2xl bg-ukgm-cream text-sm resize-none" data-testid="contact-message" />
          </div>
          <button disabled={sending} className="bg-ukgm-gold text-ukgm-blue font-bold px-6 py-3 rounded-full hover:bg-ukgm-goldDark disabled:opacity-50" data-testid="contact-submit">
            {sending ? t('contact.form.sending') : t('contact.form.submit')}
          </button>
        </form>

        <aside className="lg:col-span-2 space-y-4">
          <div className="bg-ukgm-blue text-white rounded-2xl p-6" data-testid="office-address-card">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">{t('contact.office')}</div>
            <div className="mt-3 text-sm leading-relaxed flex items-start gap-2">
              <MapPin className="w-5 h-5 text-ukgm-gold shrink-0 mt-0.5" />
              <span>{OFFICE_ADDRESS}</span>
            </div>
            <a href={MAPS_URL} target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-ukgm-gold hover:text-white"
              data-testid="open-in-maps">
              Open in Google Maps →
            </a>
          </div>

          <div className="bg-white border border-ukgm-gold/25 rounded-2xl p-6 space-y-3 text-sm">
            <a href="mailto:ukgm.trust@gmail.com" className="flex items-center gap-2 text-ukgm-blue hover:text-ukgm-gold"><Mail className="w-5 h-5 text-ukgm-gold" /> ukgm.trust@gmail.com</a>
            <a href="tel:+917447261155" className="flex items-center gap-2 text-ukgm-blue hover:text-ukgm-gold"><Phone className="w-5 h-5 text-ukgm-gold" /> +91 74472 61155</a>
            <a href={UKGM_LINKS.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-ukgm-blue hover:text-ukgm-gold"><MessageCircle className="w-5 h-5 text-ukgm-gold" /> WhatsApp us</a>
          </div>

          <div className="rounded-2xl overflow-hidden border border-ukgm-gold/25 bg-white" data-testid="office-map">
            <iframe
              title="UKGM office on Google Maps"
              src="https://maps.google.com/maps?q=Room+No+1488+BMC+Colony+A+H+Marg+Malvani+Malad+West+Mumbai+400095&z=15&output=embed"
              width="100%" height="220" style={{ border: 0 }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          <div className="bg-white border border-ukgm-gold/25 rounded-2xl p-6">
            <div className="text-xs tracking-widest uppercase text-ukgm-gold font-bold">Connect</div>
            <div className="mt-3 flex items-center gap-2">
              <a href={UKGM_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-blue hover:bg-ukgm-gold"><Instagram className="w-4 h-4" /></a>
              <a href={UKGM_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-blue hover:bg-ukgm-gold"><Facebook className="w-4 h-4" /></a>
              <a href={UKGM_LINKS.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full border border-ukgm-gold/40 flex items-center justify-center text-ukgm-blue hover:bg-ukgm-gold"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
